// File: src/components/Layout/AppShell.jsx
import React, {
  useCallback,
  useEffect,
} from "react";

import {
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";

import FuturisticSidebar from "@/components/Layout/FuturisticSidebar";

/**
 * AppShell responsibilities:
 *
 * - host the authenticated application layout
 * - render the fixed sidebar
 * - provide a lightweight UX listener for navigation events
 *
 * IMPORTANT:
 *
 * AppShell does NOT determine authentication or authorization.
 *
 * Security authority remains:
 *
 *   backend
 *      ↓
 *   /auth/me
 *      ↓
 *   AuthProvider
 *      ↓
 *   ProtectedRoute
 */
export default function AppShell() {
  const navigate =
    useNavigate();

  const location =
    useLocation();

  const goDashboard =
    useCallback(() => {
      const path =
        String(
          location.pathname ||
          ""
        );

      /*
       * Avoid redundant navigation.
       */
      if (
        path === "/dashboard"
      ) {
        return;
      }

      /*
       * AppShell should not interfere with auth/public flows
       * if it happens to still be mounted during a transition.
       */
      if (
        path.startsWith(
          "/login"
        ) ||
        path.startsWith(
          "/logout"
        )
      ) {
        return;
      }

      /*
       * replace avoids returning to a denied page through browser Back.
       */
      navigate(
        "/dashboard",
        {
          replace: true,
        }
      );
    }, [
      navigate,
      location.pathname,
    ]);

  useEffect(() => {
    /*
     * UX navigation event only.
     *
     * This event does NOT grant/revoke access.
     * ProtectedRoute/backend remain authoritative.
     */
    const onGoDashboard =
      () => {
        goDashboard();
      };

    window.addEventListener(
      "auth:go-dashboard",
      onGoDashboard
    );

    return () => {
      window.removeEventListener(
        "auth:go-dashboard",
        onGoDashboard
      );
    };
  }, [
    goDashboard,
  ]);

  return (
    <div className="min-h-screen bg-[#0b0f19] text-white">
      <FuturisticSidebar />

      <main className="ml-60 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}